import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldQuestion, Lock, Check, X, Plus, AlertTriangle, Save, ChevronDown, ChevronRight } from "lucide-react";

const REGIME_COLOR = {
  pdp_ecrit_obligatoire: "#B3382C", pdp_volontaire: "#136B79", protocole_securite: "#E49B1A",
  coordination_sps_a_analyser: "#E49B1A", donnees_insuffisantes: "#5C6E70",
  hors_champ_interference: "#5C6E70", regime_a_confirmer: "#5C6E70",
};

const BOOL_FIELDS = [
  ["entrepriseExterieurePresente", "Entreprise extérieure présente"],
  ["operationChargementDechargement", "Opération de chargement/déchargement"],
  ["chantierBatimentGenieCivil", "Chantier de bâtiment / génie civil"],
  ["plusieursEntreprises", "Plusieurs entreprises présentes"],
  ["siteEnActivite", "Site en activité"],
  ["risquesInterference", "Risques d'interférence"],
  ["sousTraitance", "Recours à la sous-traitance"],
];

function TriState({ value, onChange, testid }) {
  const v = value === true ? "oui" : value === false ? "non" : "";
  return (
    <select data-testid={testid} value={v} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "oui")}>
      <option value="">Non renseigné</option>
      <option value="oui">Oui</option>
      <option value="non">Non</option>
    </select>
  );
}

export default function QualificationPanel({ operationId, canEdit }) {
  const [data, setData] = useState(null);
  const [inputs, setInputs] = useState(null);
  const [openInputs, setOpenInputs] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ecart, setEcart] = useState({ open: false, code: null, libelle: "", justif: "" });
  const [declare, setDeclare] = useState({ open: false, libelle: "" });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/operations/${operationId}/qualification`);
      setData(r.data);
      setInputs({ schemaVersion: 1, ...(r.data.inputs || {}) });
    } catch (e) { console.warn("Qualification: chargement impossible", e); }
  }, [operationId]);

  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const q = data.qualification || {};
  const td = data.travauxDangereux || { detectes: [], declaresManuellement: [], resultatFinal: [] };
  const color = REGIME_COLOR[q.regime] || "#5C6E70";

  const up = (k, v) => setInputs((s) => ({ ...s, [k]: v }));
  const saveInputs = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/operations/${operationId}/qualification`, inputs);
      setData((d) => ({ ...d, ...r.data }));
      toast.success("Régime recalculé.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const setStatut = async (code, statut, justificationEcart = null) => {
    try {
      const r = await api.post(`/operations/${operationId}/travaux-dangereux/${code}`, { statut, justificationEcart });
      setData((d) => ({ ...d, ...r.data }));
      toast.success(`Présomption ${statut === "CONFIRME" ? "confirmée" : statut === "ECARTE" ? "écartée" : "réinitialisée"}.`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const confirmEcart = async () => {
    if (!ecart.justif.trim()) return;
    await setStatut(ecart.code, "ECARTE", ecart.justif.trim());
    setEcart({ open: false, code: null, libelle: "", justif: "" });
  };

  const confirmDeclare = async () => {
    if (!declare.libelle.trim()) return;
    try {
      const r = await api.post(`/operations/${operationId}/travaux-dangereux`, { libelle: declare.libelle.trim() });
      setData((d) => ({ ...d, ...r.data }));
      toast.success("Travail dangereux déclaré.");
      setDeclare({ open: false, libelle: "" });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const chipColor = (st) => st === "CONFIRME" ? "#B3382C" : st === "ECARTE" ? "#5C6E70" : "#E49B1A";

  return (
    <div className="card" data-testid="qualification-panel" style={{ borderLeft: `4px solid ${color}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <ShieldQuestion size={18} color={color} />
        <h3 style={{ fontSize: "1rem", margin: 0 }}>Régime réglementaire applicable</h3>
        <span data-testid="qual-regime" style={{ background: color, color: "#fff", padding: "3px 10px", borderRadius: 999, fontSize: ".78rem", fontWeight: 700 }}>{q.regimeLabel}</span>
        <span className="tag" data-testid="qual-statut">{q.statutQualification}</span>
        <span className="tag">confiance {q.confiance}</span>
        <span className="tag">v{q.qualificationVersion || q.version || 1}</span>
        {q.validationHumaineRequise && <span className="tag" data-testid="qual-humain" style={{ background: "#E49B1A", color: "#fff" }}><AlertTriangle size={12} style={{ verticalAlign: -2 }} /> Validation humaine requise</span>}
      </div>

      <p style={{ fontSize: ".86rem", color: "var(--text)", margin: "10px 0" }} data-testid="qual-justif">{q.justification}</p>
      <div style={{ fontSize: ".8rem", color: "var(--text-muted)" }}>Document/processus à ouvrir : <b>{q.documentCible}</b></div>

      {(q.criteresDeclenches || []).length > 0 && (
        <ul style={{ fontSize: ".8rem", margin: "8px 0", paddingLeft: 18, lineHeight: 1.6 }}>
          {q.criteresDeclenches.map((c, i) => (
            <li key={i}><b>{c.label}</b>{c.valeur !== null && c.valeur !== undefined ? ` : ${typeof c.valeur === "boolean" ? (c.valeur ? "oui" : "non") : c.valeur}` : ""}{c.reference ? <span style={{ color: "var(--text-muted)" }}> — {c.reference}</span> : null}</li>
          ))}
        </ul>
      )}

      {(q.informationsManquantes || []).length > 0 && (
        <div className="notice" data-testid="qual-manquants" style={{ background: "#FBF3E0", borderColor: "#E49B1A" }}>
          <b>Informations manquantes :</b>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: ".82rem", lineHeight: 1.6 }}>
            {q.informationsManquantes.map((m, i) => <li key={i}>{m.label} <span style={{ color: "var(--text-muted)" }}>— {m.pourquoi}</span></li>)}
          </ul>
        </div>
      )}

      {(q.references || []).length > 0 && (
        <div style={{ fontSize: ".74rem", color: "var(--text-muted)", marginTop: 6 }}>
          Références : {q.references.map((r) => r.code).join(" · ")}
        </div>
      )}

      {/* Travaux dangereux (présomptions à confirmer) */}
      {(td.detectes.length > 0 || td.declaresManuellement.length > 0) && (
        <div style={{ marginTop: 14 }} data-testid="qual-travaux-dangereux">
          <h4 style={{ fontSize: ".9rem", margin: "0 0 6px", display: "flex", gap: 6, alignItems: "center" }}><Lock size={14} /> Travaux dangereux (arrêté du 19/03/1993)</h4>
          {td.detectes.map((d) => (
            <div key={d.code} data-testid={`td-${d.code}`} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "6px 0", borderBottom: "1px solid var(--border, #eee)" }}>
              <span style={{ background: chipColor(d.statut), color: "#fff", padding: "1px 8px", borderRadius: 999, fontSize: ".7rem", fontWeight: 700 }}>{d.statut}</span>
              <span style={{ flex: 1, fontSize: ".84rem" }}>{d.libelle}
                <span style={{ color: "var(--text-muted)", fontSize: ".72rem" }}> — présomption (activité : {d.sourceActivite}){d.requiresHseValidation ? " · validation HSE" : ""}{d.validePar ? ` · validé par ${d.validePar}` : ""}</span>
                {d.statut === "ECARTE" && d.justificationEcart && <span style={{ color: "var(--text-muted)", fontSize: ".72rem" }}> · motif : {d.justificationEcart}</span>}
              </span>
              {canEdit && <>
                <button className="btn btn-g btn-sm" data-testid={`td-confirm-${d.code}`} onClick={() => setStatut(d.code, "CONFIRME")}><Check size={13} />Confirmer</button>
                <button className="btn btn-g btn-sm" data-testid={`td-ecart-${d.code}`} onClick={() => setEcart({ open: true, code: d.code, libelle: d.libelle, justif: "" })}><X size={13} />Écarter</button>
              </>}
            </div>
          ))}
          {td.declaresManuellement.map((d, i) => (
            <div key={`m${i}`} style={{ padding: "6px 0", fontSize: ".84rem" }}>
              <span className="tag" style={{ background: "#7A2018", color: "#fff" }}>MANUEL</span> {d.libelle} <span style={{ color: "var(--text-muted)", fontSize: ".72rem" }}>— déclaré par {d.declarePar}</span>
            </div>
          ))}
        </div>
      )}
      {canEdit && <button className="btn btn-g btn-sm" style={{ marginTop: 8 }} data-testid="td-declare" onClick={() => setDeclare({ open: true, libelle: "" })}><Plus size={13} />Déclarer un travail dangereux</button>}

      {/* Critères de qualification (saisie) */}
      {canEdit && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }} onClick={() => setOpenInputs((v) => !v)} data-testid="qual-inputs-toggle">
            {openInputs ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            <b style={{ fontSize: ".86rem", color: "var(--sarcelle, #136B79)" }}>Critères de qualification</b>
          </div>
          {openInputs && inputs && (
            <div style={{ marginTop: 10 }} data-testid="qual-inputs">
              <div className="row2">
                {BOOL_FIELDS.map(([k, lbl]) => (
                  <div key={k}><label className="f">{lbl}</label><TriState testid={`qi-${k}`} value={inputs[k]} onChange={(v) => up(k, v)} /></div>
                ))}
                <div><label className="f">Volume horaire prévisionnel cumulé (h / 12 mois)</label>
                  <input type="number" data-testid="qi-volume" value={inputs.volumeHorairePrevisionnel ?? ""} onChange={(e) => up("volumeHorairePrevisionnel", e.target.value === "" ? null : Number(e.target.value))} /></div>
                <div><label className="f">Effectif prévisionnel</label>
                  <input type="number" data-testid="qi-effectif" value={inputs.effectifPrevisionnel ?? ""} onChange={(e) => up("effectifPrevisionnel", e.target.value === "" ? null : Number(e.target.value))} /></div>
                <div><label className="f">Nature de l'opération</label>
                  <input value={inputs.natureOperation ?? ""} onChange={(e) => up("natureOperation", e.target.value || null)} /></div>
              </div>
              <label className="f">Commentaires de qualification</label>
              <textarea rows={2} value={inputs.commentairesQualification ?? ""} onChange={(e) => up("commentairesQualification", e.target.value || null)} />
              <button className="btn btn-p btn-sm" style={{ marginTop: 8 }} onClick={saveInputs} disabled={busy} data-testid="qual-save"><Save size={14} />{busy ? "Calcul…" : "Enregistrer et recalculer"}</button>
            </div>
          )}
        </div>
      )}

      <Dialog open={ecart.open} onOpenChange={(o) => !o && setEcart({ open: false, code: null, libelle: "", justif: "" })}>
        <DialogContent data-testid="ecart-dialog">
          <DialogHeader>
            <DialogTitle>Écarter une présomption de travail dangereux</DialogTitle>
            <DialogDescription>
              {ecart.libelle} — cette présomption est issue de l'arrêté du 19/03/1993. Une justification est
              obligatoire et sera tracée dans le journal des décisions.
            </DialogDescription>
          </DialogHeader>
          <label className="f">Justification de l'écart</label>
          <textarea rows={3} data-testid="ecart-justif" value={ecart.justif}
            onChange={(e) => setEcart((s) => ({ ...s, justif: e.target.value }))}
            placeholder="Ex : aucune matière combustible à proximité, poste isolé et balisé…" />
          <DialogFooter style={{ marginTop: 12, gap: 8 }}>
            <button className="btn btn-g" data-testid="ecart-cancel" onClick={() => setEcart({ open: false, code: null, libelle: "", justif: "" })}>Annuler</button>
            <button className="btn btn-p" data-testid="ecart-confirm" disabled={!ecart.justif.trim()} onClick={confirmEcart}>Écarter la présomption</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={declare.open} onOpenChange={(o) => !o && setDeclare({ open: false, libelle: "" })}>
        <DialogContent data-testid="declare-dialog">
          <DialogHeader>
            <DialogTitle>Déclarer un travail dangereux</DialogTitle>
            <DialogDescription>
              Ajoutez un travail dangereux non détecté par le moteur (arrêté du 19/03/1993). Cette déclaration est tracée.
            </DialogDescription>
          </DialogHeader>
          <label className="f">Libellé du travail dangereux</label>
          <input data-testid="declare-libelle" value={declare.libelle}
            onChange={(e) => setDeclare((s) => ({ ...s, libelle: e.target.value }))}
            placeholder="Ex : exposition à des rayonnements ionisants" />
          <DialogFooter style={{ marginTop: 12, gap: 8 }}>
            <button className="btn btn-g" data-testid="declare-cancel" onClick={() => setDeclare({ open: false, libelle: "" })}>Annuler</button>
            <button className="btn btn-p" data-testid="declare-confirm" disabled={!declare.libelle.trim()} onClick={confirmDeclare}>Déclarer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
